import type { DiGraph } from "./digraph.js";

/**
 * Brandes: undirected betweenness on the **underlying** unweighted simple graph
 * (an edge exists if any positive weight in either direction, from `toUndirectedEdgeSet`).
 * Shortest paths are in hop count. Result is **halved** for undirected convention (all pairs).
 */
export function betweennessUndirected(g: DiGraph): Map<string, number> {
  const und = g.toUndirectedEdgeSet();
  const adj = new Map<string, Set<string>>();
  for (const { u, v, w } of und) {
    if (w <= 0) continue;
    if (!adj.has(u)) adj.set(u, new Set());
    if (!adj.has(v)) adj.set(v, new Set());
    adj.get(u)!.add(v);
    adj.get(v)!.add(u);
  }
  for (const v of g.nodes) {
    if (!adj.has(v)) adj.set(v, new Set());
  }

  const nodes = g.nodes;
  const n = nodes.length;
  if (n === 0) return new Map();
  const C = new Map<string, number>();
  for (const v of nodes) C.set(v, 0);

  for (const s of nodes) {
    const S: string[] = [];
    const P = new Map<string, string[]>();
    for (const v of nodes) P.set(v, []);
    const sigma = new Map<string, number>();
    const d = new Map<string, number>();
    for (const v of nodes) {
      d.set(v, -1);
      sigma.set(v, 0);
    }
    sigma.set(s, 1);
    d.set(s, 0);
    const Q: string[] = [s];
    for (let i = 0; i < Q.length; i++) {
      const v = Q[i]!;
      S.push(v);
      for (const w of adj.get(v) ?? []) {
        if (d.get(w) === -1) {
          Q.push(w);
          d.set(w, d.get(v)! + 1);
        }
        if (d.get(w) === d.get(v)! + 1) {
          P.get(w)!.push(v);
          sigma.set(w, (sigma.get(w) ?? 0) + (sigma.get(v) ?? 0));
        }
      }
    }
    const delta = new Map<string, number>();
    for (const v of nodes) delta.set(v, 0);
    for (let i = S.length - 1; i >= 0; i--) {
      const w = S[i]!;
      for (const v of P.get(w) ?? []) {
        const sw = Math.max(sigma.get(w) ?? 0, 1e-15);
        const r = ((sigma.get(v) ?? 0) / sw) * (1 + (delta.get(w) ?? 0));
        delta.set(v, (delta.get(v) ?? 0) + r);
      }
      if (w !== s) {
        C.set(w, (C.get(w) ?? 0) + (delta.get(w) ?? 0));
      }
    }
  }

  for (const v of nodes) {
    C.set(v, (C.get(v) ?? 0) * 0.5);
  }
  return C;
}
